import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../LanguageContext';
import { FaSolarPanel, FaIndustry, FaPlug, FaShieldAlt } from 'react-icons/fa';

const Activities = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const activities = [
    { icon: FaSolarPanel, text: t.activities.activity1, color: 'from-yellow-400 to-orange-400' },
    { icon: FaIndustry, text: t.activities.activity2, color: 'from-orange-400 to-red-400' },
    { icon: FaPlug, text: t.activities.activity3, color: 'from-red-400 to-pink-400' },
  ];

  return (
    <section
      id="activities"
      ref={ref}
      className="py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden"
      data-testid="activities-section"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-64 h-64 bg-solar-500 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-64 h-64 bg-energy-500 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gradient" data-testid="activities-title">
            {t.activities.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-solar-400 to-energy-500 mx-auto rounded-full" />
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          {/* Content side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-3xl font-bold text-gray-800 mb-6">{t.activities.subtitle}</h3>
              <p className="text-lg text-gray-700 mb-6">{t.activities.description}</p>
            </div>

            <div className="space-y-4">
              {activities.map((activity, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  whileHover={{ scale: 1.03, x: 10 }}
                  className="flex items-start space-x-4 bg-white p-6 rounded-xl shadow-lg border border-gray-100"
                  data-testid={`activity-${index}`}
                >
                  <div className="flex-shrink-0">
                    <div className={`w-14 h-14 bg-gradient-to-br ${activity.color} rounded-xl flex items-center justify-center shadow-lg`}>
                      <activity.icon className="text-white text-2xl" />
                    </div>
                  </div>
                  <p className="text-base text-gray-700 flex-1 pt-2">{activity.text}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="bg-gradient-to-r from-solar-50 to-energy-50 p-6 rounded-xl border-l-4 border-solar-500"
            >
              <p className="text-sm font-semibold text-gray-600 mb-2">{t.activities.primaryActivity}</p>
              <p className="text-lg font-bold text-solar-700">{t.activities.activityCode}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 1 }}
              className="flex items-start space-x-3 bg-green-50 p-4 rounded-lg border border-green-200"
            >
              <FaShieldAlt className="text-green-600 text-xl flex-shrink-0 mt-1" />
              <p className="text-sm text-gray-700">{t.activities.compliance}</p>
            </motion.div>
          </motion.div>

          {/* Image side with energy flow animation */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img
                src="/images/activities.jpg"
                alt="Solar Panels"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              
              {/* Energy flow particles */}
              <div className="absolute inset-0">
                {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="energy-particle"
                    style={{
                      left: `${20 + i * 15}%`,
                      bottom: '20%',
                    }}
                    animate={{
                      y: [-50, -150],
                      x: [0, Math.random() * 50 - 25],
                      opacity: [0, 1, 0],
                    }}
                    transition={{
                      duration: 2 + Math.random(),
                      repeat: Infinity,
                      delay: i * 0.3,
                    }}
                  />
                ))}
              </div>

              {/* Stats overlay */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-solar-600">2020</p>
                      <p className="text-xs text-gray-600">Est.</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-energy-600">100%</p>
                      <p className="text-xs text-gray-600">Clean</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">Active</p>
                      <p className="text-xs text-gray-600">Status</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Activities;